import { html, nothing } from "lit";
import { isSubagentSessionKey } from "../../../../src/routing/session-key.js";
import type { SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import type { ChatSessionContextMeta } from "../views/chat-session-context.ts";
import {
  resolveSubagentActivityPhase,
  subagentPhaseBadgeClass,
  subagentPhaseLabel,
} from "./subagent-run-status.ts";
import { lookupSubagentCard } from "./subagents-by-session.ts";

function phaseLabel(phase?: string): string {
  if (!phase) {
    return "";
  }
  switch (phase) {
    case "thinking":
      return "Thinking";
    case "compacting":
      return "Compacting";
    case "start":
      return "Started";
    case "end":
      return "Finished";
    case "error":
      return "Error";
    default:
      return phase;
  }
}

/**
 * JAISIU-XXXX (subagent bubble redesign) — single-line "Now:" summary
 * shown on the compact bubble. The previous banner rendered the same
 * text as a full `<p>` that wrapped to multiple lines and pushed every
 * other subagent card down. Clamp to ~80 chars with an ellipsis so
 * the bubble stays one line; the full work-now text is still reachable
 * via the gear popover (the panel renders the unabridged string).
 */
const NOW_INLINE_MAX = 80;

export function resolveSubagentWorkNow(
  card: ChatSubagentCard | null | undefined,
  liveStream?: string | null,
): string {
  if (card?.toolHint?.trim()) {
    return card.toolHint.trim();
  }
  if (card?.phase?.trim()) {
    return phaseLabel(card.phase);
  }
  const stream = liveStream?.trim() || card?.streamText?.trim();
  if (stream) {
    return stream.length > 240 ? `…${stream.slice(-240)}` : stream;
  }
  if (card?.task?.trim()) {
    return card.task.trim();
  }
  return "Working…";
}

export function resolveSubagentSessionTask(
  card: ChatSubagentCard | null | undefined,
  meta: ChatSessionContextMeta | undefined,
  sessions: SessionsListResult | null,
  sessionKey: string,
): string {
  const fromCard = card?.task?.trim();
  if (fromCard) {
    return fromCard;
  }
  const fromMeta = meta?.task?.trim();
  if (fromMeta) {
    return fromMeta;
  }
  const row = sessions?.sessions?.find((s) => s.key === sessionKey);
  return row?.lastMessagePreview?.trim() || "";
}

export type SubagentSessionWorkBannerProps = {
  sessionKey: string;
  sessions: SessionsListResult | null;
  tabMeta?: Record<string, ChatSessionContextMeta>;
  cardHost: object;
  card?: ChatSubagentCard | null;
  parentKey?: string | null;
  liveStream?: string | null;
  chatSending?: boolean;
  chatRunId?: string | null;
  onReturnToParent?: (parentKey: string) => void;
};

export function resolveSubagentWorkContext(
  host: object,
  sessionKey: string,
  sessions: SessionsListResult | null,
  tabMeta?: Record<string, ChatSessionContextMeta>,
): { card: ChatSubagentCard; parentKey: string } | null {
  const key = sessionKey.trim();
  if (!key || !isSubagentSessionKey(key)) {
    return null;
  }
  return lookupSubagentCard(host, key, sessions, tabMeta);
}

/**
 * JAISIU-XXXX (subagent bubble redesign) — the chat-surfaced "subagent
 * is doing work" bubble used to be a multi-row card:
 *
 *   ┌───────────────────────────────────────────┐
 *   │ [SUBAGENT]  [In progress]                 │
 *   │ Task: <full task text, wraps freely>      │
 *   │ Now:  <full stream tail, wraps freely>    │
 *   │ [← Back to parent session]                │
 *   └───────────────────────────────────────────┘
 *
 * For chat surfaces that show several subagents at once (a fleet of 3+
 * workers, all reporting in), that layout pushes everything else off
 * the screen and dominates the conversation.
 *
 * New contract: the bubble is a single line. The only thing rendered
 * inline is the status pill + a clamped `Now:` summary. The full Task
 * text, the full work-now tail, the completion line, and the
 * "Back to parent" action are all hidden behind a small gear popover
 * (the existing `fleet-popover` component). The operator clicks the
 * gear when they actually want to read the detail; otherwise the row
 * stays ~32 px tall and visually unobtrusive.
 *
 * The two screenshots attached to JAISIU-XXXX document the before /
 * after — the after is this single-line shape, the gear icon visible
 * on the right edge of the bubble, and the popover with the same data
 * that used to be inline.
 */
export function renderSubagentSessionWorkBanner(props: SubagentSessionWorkBannerProps) {
  const key = props.sessionKey.trim();
  if (!key || !isSubagentSessionKey(key)) {
    return nothing;
  }

  const located =
    props.card && props.parentKey
      ? { card: props.card, parentKey: props.parentKey }
      : resolveSubagentWorkContext(props.cardHost, key, props.sessions, props.tabMeta);

  const meta = props.tabMeta?.[key];
  const parentKey = located?.parentKey ?? meta?.parentKey?.trim();
  const card = located?.card ?? null;
  const task = resolveSubagentSessionTask(card, meta, props.sessions, key);
  const workNow = resolveSubagentWorkNow(
    card,
    props.sessionKey === key ? props.liveStream : card?.streamText,
  );
  const phase = card ? resolveSubagentActivityPhase(card) : "ongoing";
  const runActive = Boolean(props.chatSending || props.chatRunId);

  // Clamp the inline summary so the bubble stays one line even when
  // the work-now tail is hundreds of characters long. The popover
  // still gets the full string.
  const inlineNow =
    workNow.length > NOW_INLINE_MAX ? `…${workNow.slice(-(NOW_INLINE_MAX - 1))}` : workNow;

  // Popover body slots — full Task, full Now tail, the completion line,
  // and the back-to-parent action. All of these used to render inline
  // and are now reachable on demand only.
  return html`
    <div
      class="chat-subagent-work-banner"
      data-testid="chat-subagent-work-banner"
      role="status"
      aria-live="polite"
    >
      <span class="chat-subagent-work-banner__badge">Subagent</span>
      <span
        class="chat-subagent-work-banner__status chat-subagent-work-banner__status--${subagentPhaseBadgeClass(phase)}"
        data-testid="chat-subagent-work-banner__status"
      >
        ${subagentPhaseLabel(phase)}
      </span>
      <span
        class="chat-subagent-work-banner__now"
        data-testid="chat-subagent-work-banner__now"
        title=${workNow}
        >${inlineNow}</span
      >
      <fleet-popover
        class="chat-subagent-work-banner__menu"
        data-testid="chat-subagent-work-banner__menu"
      >
        <div class="chat-subagent-work-banner__panel">
          <div class="chat-subagent-work-banner__panel-section">
            <span class="chat-subagent-work-banner__panel-label">Task</span>
            <p
              class="chat-subagent-work-banner__panel-text"
              data-testid="chat-subagent-work-banner__task"
            >
              ${task || html`<span class="muted">No task recorded</span>`}
            </p>
          </div>
          <div class="chat-subagent-work-banner__panel-section">
            <span class="chat-subagent-work-banner__panel-label">Now</span>
            <p
              class="chat-subagent-work-banner__panel-text"
              data-testid="chat-subagent-work-banner__now-full"
            >
              ${workNow}
            </p>
          </div>
          ${
            !runActive && phase === "completed"
              ? html`
                  <p
                    class="chat-subagent-work-banner__panel-done muted"
                    data-testid="chat-subagent-work-banner__done"
                  >
                    Run finished — see parent chat for summary.
                  </p>
                `
              : nothing
          }
          ${
            parentKey && props.onReturnToParent
              ? html`
                  <button
                    type="button"
                    class="btn chat-subagent-work-banner__back"
                    data-testid="chat-subagent-work-banner__back"
                    @click=${() => props.onReturnToParent?.(parentKey)}
                  >
                    ← Back to parent session
                  </button>
                `
              : nothing
          }
        </div>
      </fleet-popover>
    </div>
  `;
}
